import React from "react";
import { Card } from "primereact/card";

type Severity = "success" | "warning" | "danger" | "info";

interface KpiStatCardProps {
  title:       string;
  value:       string | number;
  subtitle?:   string;
  growth?:     number;
  severity?:   Severity;
  icon:        string;
  onClick?:    () => void;
}

const severityStyles: Record<Severity, { border: string; icon: string }> = {
  success: { border: "#22c55e", icon: "text-green-500" },
  warning: { border: "#f59e0b", icon: "text-yellow-500" },
  danger:  { border: "#ef4444", icon: "text-red-500"   },
  info:    { border: "#3b82f6", icon: "text-blue-500"  },
};

const KpiStatCard: React.FC<KpiStatCardProps> = ({
  title, value, subtitle, growth, severity = "info", icon, onClick,
}) => {
  const style = severityStyles[severity];
  const hasGrowth = growth !== undefined;

  return (
    <Card
      className="h-full"
      style={{
        borderTop: `4px solid ${style.border}`,
        cursor: onClick ? "pointer" : undefined,
        transition: onClick ? "box-shadow 0.15s" : undefined,
      }}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } } : undefined}
      onMouseEnter={onClick ? (e) => { (e.currentTarget as HTMLElement).style.boxShadow = "0 4px 16px rgba(0,0,0,0.12)"; } : undefined}
      onMouseLeave={onClick ? (e) => { (e.currentTarget as HTMLElement).style.boxShadow = ""; } : undefined}
    >
      <div className="flex justify-content-between align-items-start">
        <div>
          <p className="text-color-secondary text-xs font-semibold uppercase mb-2"
             style={{ letterSpacing: "0.05em" }}>
            {title}
          </p>
          <p className="text-3xl font-bold text-color m-0">{value}</p>
          {subtitle && (
            <p className="text-xs text-color-secondary mt-1 mb-0">{subtitle}</p>
          )}
          {hasGrowth && (
            <span
              className={`text-xs font-semibold mt-2 inline-block px-2 py-1 border-round ${
                growth! >= 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-600"
              }`}
            >
              <i className={`pi ${growth! >= 0 ? "pi-arrow-up" : "pi-arrow-down"} mr-1`} />
              {Math.abs(growth!).toFixed(1)}% vs prev week
            </span>
          )}
        </div>
        <div
          className={`flex align-items-center justify-content-center border-round`}
          style={{ width: 44, height: 44, background: `${style.border}18` }}
        >
          <i className={`${icon} text-xl ${style.icon}`} />
        </div>
      </div>
    </Card>
  );
};

export default KpiStatCard;
